import { useRoute } from '@react-navigation/native';


import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  fetchUserAttributes


} from 'aws-amplify/auth';
import {Alert} from 'react-native';


// Amplify.configure() is called once, in App.js — see auth/amplifyConfig.js.
// Optional fallback to classic Auth API if needed
let AuthFallback = null;
try {
  // Dynamically require to avoid bundling when not needed
  // eslint-disable-next-line global-require
  AuthFallback = require('aws-amplify').Auth;
} catch (_) {
  // ignore if not available
}

const AuthHelper = {
  // Sign up a user
  async signUp({ username,password, email, phone_number}) {
    return await signUp({
      username,
      email,
      password,
      phone_number,
      options: {
        userAttributes: { email: email, phone_number: phone_number}
      }
    });
  },

  // Confirm sign up
  async confirmSignUp({ username, code }) {
    return await confirmSignUp({ username, confirmationCode: code });
  },

  // Resend confirmation code
  async resendCode({ username }) {
    return await resendSignUpCode({ username });
  },

  // Sign in
  async signIn({ username, password }) {
    // Returns nextStep for MFA/challenges if configured
    return await signIn({ username, password });
  },

  // Sign out
  async signOut() {
    return await signOut();
  },

  // Get current user (or null if not signed in)
  async getCurrentUser() {
    try {
      return await getCurrentUser();
    } catch {
      return null;
    }
  },

  // Get tokens/session for API calls
  async getSession() {
    return await fetchAuthSession(); // tokens?.idToken, tokens?.accessToken, tokens?.refreshToken
  },

  async doConfirmMfa({ code }) {
    return await confirmSignIn({ challengeResponse: code });
  },


  // Step 1: Send the verification code to the user’s email/phone (based on your pool settings)
  async requestPasswordReset1(username) {
    const result = await resetPassword({ username });
    // Optional info about where the code was sent
    const deliveryMedium = result?.codeDeliveryDetails?.deliveryMedium;
    const destination = result?.codeDeliveryDetails?.destination;
    return {
      step: 'code-sent',
      deliveryHint: deliveryMedium && destination ? `${deliveryMedium} to ${destination}` : null,
    };
  },

  // New: Reset password - request a code
  async requestPasswordReset({ username }) {
    const u = (username || '').trim();
    if (!u) {
      throw new Error('Username is required');
    }
    const result = await resetPassword({ username: u });

    // For convenience, also surface codeDeliveryDetails at the top level if present in nextStep
    const codeDeliveryDetails =
      result?.nextStep?.codeDeliveryDetails ?? result?.codeDeliveryDetails ?? null;

    return codeDeliveryDetails ? { ...result, codeDeliveryDetails } : result;
  },

  // New: Confirm password reset with code and new password
  // Supports both:
  //   confirmPasswordReset({ username }, { confirmationCode }, { newPassword })
  // and
  //   confirmPasswordReset({ username, confirmationCode, newPassword })
  async confirmPasswordReset(a, b, c) {
    let username;
    let confirmationCode;
    let newPassword;

    if (
      typeof a === 'object' &&
      (a?.confirmationCode || a?.newPassword || (a?.username && !b && !c))
    ) {
      // Single-object signature
      username = a?.username || a?.email;
      confirmationCode = a?.confirmationCode || a?.code;
      newPassword = a?.newPassword || a?.password;
    } else {
      // Three-argument signature
      username = typeof a === 'object' ? a?.username || a?.email : a;
      confirmationCode = typeof b === 'object' ? b?.confirmationCode || b?.code : b;
      newPassword = typeof c === 'object' ? c?.newPassword || c?.password : c;
    }

    username = (username || '').trim();

    if (!username || !confirmationCode || !newPassword) {
      throw new Error('Username, confirmation code, and new password are required');
    }

    return await confirmResetPassword({ username, confirmationCode, newPassword });
  },

  /**
   * Internal: fetch user attributes using v6 or classic Auth as fallback.
   */
  async getUserAttributesSafe() {
    // Try v6 functional API
    try {
      const attrs = await fetchUserAttributes();
      if (attrs && typeof attrs === 'object') return attrs;
    } catch (_) {
      // continue to fallback
    }

    // Fallback to classic Auth API (for environments still using it)
    if (AuthFallback) {
      try {
        const user = await AuthFallback.currentAuthenticatedUser();
        const list = await AuthFallback.userAttributes(user); // [{ Name, Value }, ...]
        const map = {};
        for (const { Name, Value } of list || []) {
          map[Name] = Value;
        }
        return map;
      } catch (_) {
        // not authenticated or not available
      }
    }

    return null;
  },

  /**
   * Get the current user's email and phone (phone_number in Cognito).
   * Returns { email: string|null, phone: string|null } or nulls if not signed in.
   */
  async getEmailAndPhone() {
    const attrs = await this.getUserAttributesSafe();
    const email = attrs?.email ?? null;
    // Cognito stores phone as "phone_number" (E.164); include common fallbacks just in case.
    const phone = attrs?.phone_number ?? attrs?.phone ?? null;
    return { email, phone };
  },



};

export default AuthHelper;
